/**
 * Game setup page (create and edit) — the working copy and everything derived
 * from it, kept pure so the page and both screens share one definition and the
 * tests can pin it without rendering.
 *
 * Edit batches every change until Save. `setupPatches` splits the draft into
 * the two server calls: Game columns go to PATCH /settings, the name, map and
 * rules to PATCH /setup.
 */
import {
  BOOT_TIMER_OPTIONS,
  cardBonusScheduleInfo,
  fogOfWarOf,
  gameConfigWithDefaults,
  mapSeatRange,
  validateTeamFormat,
  withFogPlacement,
  type GameConfig,
  type GameResponse,
  type MapDef,
  type UpdateGameSettingsRequest,
} from '@warcarp/api-types'
import { teamsConfigOf } from '@warcarp/api-types'
import { describeFixedSetup } from './fixedSetup'
import { computeSeatMap } from './seatMap'
import { teamsRuleValue, teamsShortLabel } from './teamSetup'

/** Map + rules: the part of the draft PATCH /setup takes as `mapId` + `config`. */
export interface GameSetupValue {
  mapId: string
  config: Partial<GameConfig>
}

export interface GameSetupDraft extends GameSetupValue {
  name: string
  isPublic: boolean
  maxPlayers: number
  autoStart: boolean
  bootAfterSeconds: number
}

/** The rules the setup page edits, in page order. */
const CONFIG_KEYS = [
  'fogOfWar', 'autoAssignTerritories', 'startingTroops', 'maxAttacksPerTurn',
  'maxFortifiesPerTurn', 'eliminatePlayerBonus', 'cardBonusSchedule',
] as const satisfies ReadonlyArray<keyof GameConfig>
type ConfigKey = (typeof CONFIG_KEYS)[number]

/** Game columns, saved through PATCH /settings. */
const SETTINGS_KEYS = ['maxPlayers', 'autoStart', 'isPublic', 'bootAfterSeconds'] as const

export type SetupFieldKey = 'name' | 'mapId' | 'teams' | (typeof SETTINGS_KEYS)[number] | ConfigKey

export interface SetupChange {
  key: SetupFieldKey
  /** The page's label, for the desktop Changes list. */
  label: string
  /** Lower-case short name, for the one-line footer. */
  short: string
  from: string
  to: string
}

const unlimited = (v: number | null) => (v === null ? 'Unlimited' : String(v))
const FOG_LABELS = { none: 'None', light: 'Light', heavy: 'Heavy' } as const
export const bootTimerLabel = (seconds: number) =>
  BOOT_TIMER_OPTIONS.find((option) => option.seconds === seconds)?.label ?? `${Math.round(seconds / 3600)} hours`

const FIELDS: Record<SetupFieldKey, { label: string; short: string; format: (v: never) => string }> = {
  name: { label: 'Title', short: 'title', format: (v: string) => v.trim() || 'Untitled' },
  isPublic: { label: 'Visibility', short: 'visibility', format: (v: boolean) => (v ? 'Public' : 'Private') },
  teams: { label: 'Mode', short: 'mode', format: (v: GameConfig['teams']) => (v ? teamsRuleValue(v) : 'Individual') },
  maxPlayers: { label: 'Players', short: 'players', format: (v: number) => String(v) },
  autoStart: { label: 'Auto-start', short: 'auto-start', format: (v: boolean) => (v ? 'On' : 'Off') },
  bootAfterSeconds: { label: 'Boot timer', short: 'boot timer', format: bootTimerLabel },
  mapId: { label: 'Map', short: 'map', format: (v: string) => v },
  fogOfWar: { label: 'Fog of war', short: 'fog', format: (v: keyof typeof FOG_LABELS) => FOG_LABELS[v] },
  autoAssignTerritories: { label: 'Territory selection', short: 'territory selection', format: (v: boolean) => (v ? 'Random' : 'Manual') },
  startingTroops: { label: 'Starting troops', short: 'starting troops', format: (v: number) => String(v) },
  maxAttacksPerTurn: { label: 'Attacks', short: 'attacks', format: unlimited },
  maxFortifiesPerTurn: { label: 'Fortifies', short: 'fortifies', format: unlimited },
  eliminatePlayerBonus: { label: 'Eliminate bonus', short: 'eliminate bonus', format: (v: number) => `+${v}` },
  cardBonusSchedule: { label: 'Card sets', short: 'card sets', format: (v: string) => cardBonusScheduleInfo(v).label },
}

/**
 * A setting's page label and readable value (e.g. a value a map pins); null
 * for a key the page doesn't show.
 */
export function describeSetupValue(key: string, value: unknown): { label: string; value: string } | null {
  if (!(key in FIELDS)) return null
  const field = FIELDS[key as SetupFieldKey]
  return { label: field.label, value: (field.format as (v: unknown) => string)(value) }
}

/** The rules as they will play, with fog's forced placement applied. */
function effectiveRules(config: Partial<GameConfig>): Pick<GameConfig, ConfigKey> {
  const effective = gameConfigWithDefaults(withFogPlacement(config))
  return { ...effective, fogOfWar: fogOfWarOf(config) }
}

export function draftFromGame(game: Pick<GameResponse, 'name' | 'isPublic' | 'maxPlayers' | 'autoStart' | 'bootAfterSeconds' | 'mapId' | 'setupConfig'>): GameSetupDraft {
  return {
    name: game.name ?? '',
    isPublic: game.isPublic,
    maxPlayers: game.maxPlayers,
    autoStart: game.autoStart,
    bootAfterSeconds: game.bootAfterSeconds,
    mapId: game.mapId ?? 'carpathia',
    config: (game.setupConfig as Partial<GameConfig> | null) ?? {},
  }
}

/**
 * What the host changed, in page order. Rules compare as they will play (so
 * an explicit default, or flipping a value back, is not a change).
 */
export function setupChanges(base: GameSetupDraft, draft: GameSetupDraft, mapName: (id: string) => string = (id) => id): SetupChange[] {
  const changes: SetupChange[] = []
  const add = (key: SetupFieldKey, from: unknown, to: unknown) => {
    if (from === to) return
    const field = FIELDS[key]
    const format = (key === 'mapId' ? mapName : field.format) as (v: unknown) => string
    changes.push({ key, label: field.label, short: field.short, from: format(from), to: format(to) })
  }
  if (draft.name.trim() !== base.name.trim()) add('name', base.name, draft.name)
  add('isPublic', base.isPublic, draft.isPublic)
  // Team format: compared as its label, so an identical format is no change.
  const teamsKey = (c: Partial<GameConfig>) => FIELDS.teams.format(teamsConfigOf(c) as never)
  if (teamsKey(base.config) !== teamsKey(draft.config)) add('teams', teamsConfigOf(base.config), teamsConfigOf(draft.config))
  add('maxPlayers', base.maxPlayers, draft.maxPlayers)
  add('autoStart', base.autoStart, draft.autoStart)
  add('mapId', base.mapId, draft.mapId)
  const before = effectiveRules(base.config)
  const after = effectiveRules(draft.config)
  add('fogOfWar', before.fogOfWar, after.fogOfWar)
  add('bootAfterSeconds', base.bootAfterSeconds, draft.bootAfterSeconds)
  for (const key of CONFIG_KEYS) if (key !== 'fogOfWar') add(key, before[key], after[key])
  return changes
}

/** "1 change: players 4 → 3" / "2 changes: players 5 → 4 · fog None → Light". */
export function changesFooterText(changes: readonly SetupChange[]): string {
  if (changes.length === 0) return 'No changes yet'
  return `${changes.length} ${changes.length === 1 ? 'change' : 'changes'}: ` +
    changes.map((c) => `${c.short} ${c.from} → ${c.to}`).join(' · ')
}

export interface SetupPatches {
  settings: UpdateGameSettingsRequest | null
  setup: { name?: string; mapId?: string; config?: Partial<GameConfig> } | null
  /**
   * PATCH /settings checks the seat count against the map the game has NOW,
   * so it goes first unless the new count only fits the new map; then the map
   * has to change first. It also goes second when it would fill a team lobby:
   * a full team lobby starts at once, so the rest of the edit (fog, rules,
   * name, map) must already be saved.
   */
  settingsFirst: boolean
}

/** The two minimal PATCH bodies, so concurrent edits don't overwrite untouched fields. */
export function setupPatches(
  base: GameSetupDraft,
  draft: GameSetupDraft,
  baseMap?: Pick<MapDef, 'territories' | 'fixedSetup'> | null,
  /** Players seated in the lobby now. When given, a settings patch that fills a team lobby goes last. */
  seated?: number,
): SetupPatches {
  const settings: UpdateGameSettingsRequest = {}
  for (const key of SETTINGS_KEYS) {
    if (draft[key] !== base[key]) (settings as Record<string, unknown>)[key] = draft[key]
  }
  const setup: NonNullable<SetupPatches['setup']> = {}
  // Empty clears the name server-side (stored null).
  if (draft.name.trim() !== base.name.trim()) setup.name = draft.name.trim()
  if (draft.mapId !== base.mapId) setup.mapId = draft.mapId
  const before = effectiveRules(base.config)
  const after = effectiveRules(draft.config)
  const config: Partial<GameConfig> = {}
  // 'none' is sent explicitly: setup PATCHes merge, so omitting it would keep
  // a previous Light/Heavy.
  for (const key of CONFIG_KEYS) if (before[key] !== after[key]) (config as Record<string, unknown>)[key] = after[key]
  if (Object.keys(config).length > 0) setup.config = config
  // Team format goes through PATCH /settings, the one call that can send
  // `teams: null` (back to Individual) and the seat count with it.
  const baseTeams = teamsConfigOf(base.config)
  const draftTeams = teamsConfigOf(draft.config)
  if (baseTeams?.size !== draftTeams?.size || baseTeams?.assignment !== draftTeams?.assignment) {
    settings.teams = draftTeams ?? null
    if (draftTeams) settings.maxPlayers = draft.maxPlayers
  }
  const oldRange = mapSeatRange(baseMap)
  const fitsOldMap = draftTeams
    ? validateTeamFormat({ size: draftTeams.size, players: draft.maxPlayers, map: baseMap }).ok
    : draft.maxPlayers >= oldRange.min && draft.maxPlayers <= oldRange.max
  // The server starts a team lobby the moment its seats are all filled
  // (PATCH /settings auto-start), so the call that fills it goes last and the
  // game never starts before the whole edit is saved.
  const fillsTeamLobby = !!draftTeams && seated !== undefined && settings.maxPlayers !== undefined && seated >= settings.maxPlayers
  return {
    settings: Object.keys(settings).length > 0 ? settings : null,
    setup: Object.keys(setup).length > 0 ? setup : null,
    settingsFirst: !fillsTeamLobby && (!setup.mapId || settings.maxPlayers === undefined || !baseMap || fitsOldMap),
  }
}

/**
 * The seat count's lower bound in a lobby: the highest occupied or reserved
 * seat (slots can have holes), so a high-slot player is never orphaned. The
 * server enforces the same floor.
 */
export function lobbySeats(game: Pick<GameResponse, 'players' | 'invitations' | 'maxPlayers' | 'minPlayers'> & { setupConfig?: GameResponse['setupConfig'] }): { floor: number; seated: number } {
  const taken = computeSeatMap(game.players, game.invitations, game.maxPlayers).filter((s) => s.kind !== 'open')
  const highest = Math.max(0, ...taken.map((s) => s.slot))
  return {
    // A team lobby's minPlayers is its seat count (it starts full), so only
    // the taken seats bound it; the server uses the same floor.
    floor: teamsConfigOf(game.setupConfig) ? highest : Math.max(game.minPlayers, highest),
    seated: taken.length,
  }
}

/** Board-dictated maps carry special rules; plain maps have none worth a card. */
export function hasSpecialRules(map: Pick<MapDef, 'specialRules' | 'fixedSetup'> | null | undefined): boolean {
  return !!map && (!!map.fixedSetup || (map.specialRules?.length ?? 0) > 0)
}

/** "43 territories · 13 regions · 2–6 players". */
export function mapFacts(map: Pick<MapDef, 'territories' | 'regions' | 'fixedSetup'>): string {
  const range = mapSeatRange(map)
  const players = range.min === range.max ? `${range.max} players` : `${range.min}–${range.max} players`
  return `${map.territories.length} territories · ${map.regions.length} regions · ${players}`
}

/** Key/value lines for the desktop "Your game" card; the footer joins all but Map. */
export function setupSummaryLines(draft: GameSetupDraft, map: MapDef | null | undefined, mapName: string): Array<{ label: string; value: string }> {
  const special = hasSpecialRules(map)
  const fixed = map ? describeFixedSetup(map) : null
  const rules = gameConfigWithDefaults({ ...withFogPlacement(draft.config), ...map?.fixedSetup?.config })
  const fog = fogOfWarOf(draft.config)
  const fmt = (v: number | null) => (v === null ? '∞' : String(v))
  const teams = teamsConfigOf(draft.config)
  return [
    { label: 'Map', value: `${mapName}${special ? ' ★' : ''}` },
    teams
      ? { label: 'Teams', value: teamsShortLabel(teams, draft.maxPlayers) }
      : { label: 'Players', value: `${draft.maxPlayers} · ${draft.autoStart ? 'auto-start' : 'manual start'}` },
    { label: 'Fog · pace', value: `${fog === 'none' ? 'No fog' : `${FOG_LABELS[fog]} fog`} · ${bootTimerLabel(draft.bootAfterSeconds)}` },
    { label: 'Board', value: fixed ? fixed.homeArea : `${rules.autoAssignTerritories ? 'Random' : 'Manual'} · ${rules.startingTroops} troops` },
    { label: 'Turns · rewards', value: `${fmt(rules.maxAttacksPerTurn)}/${fmt(rules.maxFortifiesPerTurn)} · +${rules.eliminatePlayerBonus} · ${rules.cardBonusSchedule}` },
  ]
}

/** One line for the phone/tablet footer: everything but the map, which is on screen. */
export function setupSummaryText(lines: ReadonlyArray<{ label: string; value: string }>): string {
  return lines.slice(1).map((l) => l.value).join(' · ')
}
